import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { createClient } from "@/lib/supabase/client";
import config from "@/config";

const supabase = createClient();

const ADMIN_USER_ID =
  process.env.NEXT_PUBLIC_ADMIN_USER_ID ||
  "1fb961c1-c13c-4b31-9544-bad91da8947e";

function deepMerge(target, source) {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = deepMerge(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

const fetchAndMergeTranslations = async (
  locale,
  namespace,
  userId = null,
  mergeWithAdminTranslations = false
) => {
  let finalTranslations;

  let query = supabase
    .from("translations")
    .select("key, value, user_id")
    .eq("locale", locale)
    .eq("namespace", namespace);

  if (mergeWithAdminTranslations) {
    const userIds = [ADMIN_USER_ID];
    if (userId) userIds.push(userId);
    query = query.in("user_id", userIds);
  } else if (userId) {
    query = query.eq("user_id", userId);
  } else {
    query = query.eq("user_id", ADMIN_USER_ID);
  }

  const { data, error } = await query;

  if (error) {
    console.error(`Error fetching translations for ${namespace}:`, error);
    return {};
  }

  const adminTranslations = {};
  const userTranslations = {};

  data.forEach(({ key, value, user_id }) => {
    if (user_id === ADMIN_USER_ID) {
      adminTranslations[key] = value;
    } else {
      userTranslations[key] = value;
    }
  });

  if (mergeWithAdminTranslations) {
    finalTranslations = deepMerge(adminTranslations, userTranslations);
  } else {
    finalTranslations = userId ? userTranslations : adminTranslations;
  }

  const filterEmptyTranslations = (obj) => {
    const filtered = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === "object") {
        const nestedFiltered = filterEmptyTranslations(value);
        if (Object.keys(nestedFiltered).length > 0) {
          filtered[key] = nestedFiltered;
        }
      } else if (value && value.trim() !== "") {
        filtered[key] = value;
      }
    }
    return filtered;
  };

  const finalFilteredTranslations = filterEmptyTranslations(finalTranslations);

  return finalFilteredTranslations;
};

const fetchTranslations = async (locale, namespace, userId = null) => {
  return fetchAndMergeTranslations(locale, namespace, userId, true);
};

const customBackend = {
  type: "backend",
  init: () => {},
  read: (locale, namespace, callback) => {
    callback(null, {});
  },
};

i18n
  .use(customBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: "en-GB",
    supportedLngs: config.locales,
    ns: ["app", "widget/message", "widget/products", "widget/request"],
    defaultNS: "app",
    interpolation: {
      escapeValue: false,
    },
    backend: customBackend,
    debug: false,
  });

const loadTranslations = async (locale, namespace = "app", userId = null) => {
  const translations = await fetchTranslations(locale, namespace, userId);
  i18n.addResourceBundle(
    locale,
    namespace,
    translations[namespace] || {},
    true,
    true
  );
};

const loadPublicWidgetTranslations = async (
  locale,
  namespace,
  embedIdentifier
) => {
  const response = await fetch(
    `/api/embed/${encodeURIComponent(embedIdentifier)}/translations?locale=${encodeURIComponent(locale)}&namespace=${encodeURIComponent(namespace)}`
  );
  if (!response.ok) {
    throw new Error(`Failed to load public ${namespace} translations`);
  }
  const { translations = {} } = await response.json();
  i18n.addResourceBundle(locale, namespace, translations, true, true);
};

export {
  i18n,
  loadTranslations,
  loadPublicWidgetTranslations,
  fetchAndMergeTranslations,
};
